import React from 'react';
import { motion } from 'framer-motion';
import { Blocks, FileCode, ClipboardPaste, CheckCircle, PenLine, Zap, ShieldCheck, BarChart3, Copy, Globe } from 'lucide-react';
export function WorkflowSection() {
  const steps = [{
    icon: <Blocks className="w-6 h-6" data-id="element-239" />,
    title: 'Create page in CMS',
    desc: 'Empinity builds the page using a modular page builder with predefined, design-approved components.'
  }, {
    icon: <FileCode className="w-6 h-6" data-id="element-240" />,
    title: 'CMS generates code',
    desc: 'The system outputs a short script and a block of HTML — ready to use.'
  }, {
    icon: <ClipboardPaste className="w-6 h-6" data-id="element-241" />,
    title: 'Paste into AEM',
    desc: 'A new AEM page is created as usual. Instead of building content in AEM, the generated code is pasted into an HTML element.'
  }, {
    icon: <CheckCircle className="w-6 h-6" data-id="element-242" />,
    title: 'Review & publish',
    desc: 'The page is reviewed and published through the standard AEM workflow.'
  }];
  const benefits = [{
    icon: <PenLine className="w-4 h-4" data-id="element-243" />,
    text: 'All future edits happen in CMS — no need to touch AEM again'
  }, {
    icon: <Copy className="w-4 h-4" data-id="element-244" />,
    text: 'Pages can be cloned — only change variables (model, price, etc.)'
  }, {
    icon: <Globe className="w-4 h-4" data-id="element-245" />,
    text: 'Global constants (e.g. interest rate) — change once, applies everywhere'
  }, {
    icon: <Zap className="w-4 h-4" data-id="element-246" />,
    text: 'Faster campaign execution'
  }, {
    icon: <ShieldCheck className="w-4 h-4" data-id="element-247" />,
    text: 'Fewer manual errors'
  }, {
    icon: <BarChart3 className="w-4 h-4" data-id="element-248" />,
    text: 'Consistency across markets'
  }];
  return <section className="h-full flex items-center py-8 px-4 sm:px-6 lg:px-8 bg-gray-50/50 overflow-y-auto lg:overflow-hidden" data-id="element-249">
      <div className="max-w-6xl mx-auto w-full" data-id="element-250">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        ease: 'easeOut'
      }} className="mb-8" data-id="element-251">
          <span className="text-xs font-bold tracking-widest text-[#80BC41] uppercase mb-3 block" data-id="element-252">
            04 — Workflow
          </span>
          <h2 className="text-3xl md:text-5xl font-bold text-[#1a1a1a] font-heading" data-id="element-253">
            How it works in practice
          </h2>
        </motion.div>

        <div className="grid lg:grid-cols-4 gap-4 mb-8" data-id="element-254">
          {steps.map((step, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.5,
          delay: 0.1 + idx * 0.08,
          ease: 'easeOut'
        }} className="relative bg-white p-5 rounded-xl border border-gray-100 shadow-sm" data-id="element-255">
              <div className="flex lg:flex-col items-start gap-3" data-id="element-256">
                <div className="w-10 h-10 rounded-lg bg-[#1a1a1a] text-white flex items-center justify-center shrink-0" data-id="element-257">
                  {step.icon}
                </div>
                <div data-id="element-258">
                  <div className="flex items-center gap-2 mb-1" data-id="element-259">
                    <span className="text-[10px] font-bold text-[#EB0A1E] uppercase tracking-wider" data-id="element-260">
                      Step {idx + 1}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-[#1a1a1a] mb-1" data-id="element-261">
                    {step.title}
                  </h3>
                  <p className="text-xs text-gray-500 leading-relaxed" data-id="element-262">
                    {step.desc}
                  </p>
                </div>
              </div>
              {idx < steps.length - 1 && <div className="hidden lg:block absolute top-1/2 -right-3 w-2 h-2 border-t-2 border-r-2 border-gray-300 rotate-45 -translate-y-1/2" data-id="element-263"></div>}
            </motion.div>)}
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5,
        delay: 0.5,
        ease: 'easeOut'
      }} className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-id="element-264">
          <h4 className="text-xs font-bold tracking-widest text-gray-400 uppercase mb-4" data-id="element-265">
            Key Benefits
          </h4>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-266">
            {benefits.map((b, idx) => <div key={idx} className="flex items-start gap-3 bg-gray-50 rounded-xl p-3.5" data-id="element-267">
                <div className="w-7 h-7 rounded-lg bg-[#80BC41]/10 flex items-center justify-center text-[#80BC41] shrink-0 mt-0.5" data-id="element-268">
                  {b.icon}
                </div>
                <span className="text-xs font-bold text-[#1a1a1a] leading-snug" data-id="element-269">
                  {b.text}
                </span>
              </div>)}
          </div>
        </motion.div>
      </div>
    </section>;
}